import React, { useEffect, useMemo, useState } from "react";
import user_service from "../service/user_service";
import Pic from "../img/pic.png";
import "./RecipientPicker.css";

function RecipientAvatar({ src, alt, fallback }) {
  if (src && src !== "image") {
    return <img className="recipient-picker__avatar" src={src} alt={alt || ""} />;
  }
  return (
    <div className="recipient-picker__avatar recipient-picker__avatar--placeholder" aria-hidden>
      {fallback || "?"}
    </div>
  );
}

function SelectedChip({ avatarSrc, avatarFallback, name, sub, onRemove, disabled }) {
  return (
    <div className="recipient-picker__chip">
      <RecipientAvatar src={avatarSrc} alt={name} fallback={avatarFallback} />
      <div className="recipient-picker__meta">
        <span className="recipient-picker__name">{name}</span>
        {sub ? <span className="recipient-picker__sub">{sub}</span> : null}
      </div>
      {!disabled && onRemove ? (
        <button
          type="button"
          className="recipient-picker__remove"
          title="Remove"
          aria-label={`Remove ${name}`}
          onClick={onRemove}
        >
          <i className="fi-trash" />
        </button>
      ) : null}
    </div>
  );
}

/**
 * Shared recipient picker (Associates / Group/Team / Office) used by newsletter and bulk email flows.
 */
export default function RecipientPicker({
  contactsAssociate,
  setContactsAssociate,
  contactsGroups,
  setContactsGroups,
  officeGroups,
  setOfficeGroups,
  disabled = false,
  helperText = "",
}) {
  const [pickerMode, setPickerMode] = useState("");
  const [contactName, setContactName] = useState("");
  const [getContact, setGetContact] = useState([]);
  const [contactGroupMember, setContactGroupMember] = useState([]);
  const [organizationGet, setOrganizationGet] = useState({ data: [] });

  const contactStatus = "active";

  const selectedCount =
    contactsAssociate.length + contactsGroups.length + officeGroups.length;

  const SearchGetAll = async () => {
    try {
      const response = await user_service.SearchContactfilterAssociate(
        1,
        pickerMode,
        contactStatus,
        contactName
      );
      if (response) {
        setGetContact(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching contacts:", error);
    }
  };

  useEffect(() => {
    if (contactName && pickerMode === "associate") {
      SearchGetAll();
    } else if (!contactName) {
      setGetContact([]);
    }
  }, [pickerMode, contactName]);

  useEffect(() => {
    ContactGroupMemberGetAll();
    OrganizationTreeGets();
  }, []);

  const ContactGroupMemberGetAll = async () => {
    try {
      const response = await user_service.contactGroupMemberGetAll();
      if (response?.data) {
        setContactGroupMember(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching groups:", error);
    }
  };

  const OrganizationTreeGets = async () => {
    try {
      if (!localStorage.getItem("auth")) return;
      const response = await user_service.organizationTreeGet();
      if (response) {
        setOrganizationGet(response.data || { data: [] });
      }
    } catch (error) {
      console.error("Error fetching organization tree:", error);
    }
  };

  const handleContactAssociate = (contactAssociate) => {
    if (disabled) return;
    setContactsAssociate((prev) => [...prev, contactAssociate]);
    setGetContact([]);
    setContactName("");
  };

  const handleGroupData = (data) => {
    if (disabled) return;
    setContactsGroups((prev) => [...prev, data]);
  };

  const handleOffice = (item) => {
    if (disabled) return;
    setOfficeGroups((prev) => [...prev, item]);
  };

  const groupLabel = (group) => {
    if (group?.groupDetails?.group_name) return group.groupDetails.group_name;
    if (group?.group_member?.length) return `Group (${group.group_member.length} members)`;
    return "Group";
  };

  const groupOfficeLabel = (group) => {
    if (group?.groupDetails?.groupOffice) {
      return `Integra Realty — ${group.groupDetails.groupOffice}`;
    }
    return "Team";
  };

  const browsePlaceholder = useMemo(() => {
    if (!pickerMode) return "Choose a recipient type above to browse and add people.";
    if (pickerMode === "associate" && !contactName) {
      return "Type a name to search for associates.";
    }
    if (pickerMode === "associate" && contactName && !getContact.length) {
      return "No associates match your search.";
    }
    if (pickerMode === "group" && !contactGroupMember.length) {
      return "No groups available.";
    }
    if (pickerMode === "office" && !organizationGet.data?.length) {
      return "No offices available.";
    }
    return null;
  }, [pickerMode, contactName, getContact.length, contactGroupMember.length, organizationGet.data]);

  return (
    <div className={`recipient-picker${disabled ? " recipient-picker--disabled" : ""}`}>
      <div className="recipient-picker__header">
        <h4 className="recipient-picker__title">Recipients</h4>
        {helperText ? <p className="recipient-picker__hint">{helperText}</p> : null}
      </div>

      <div className="recipient-picker__grid">
        <div className="recipient-picker__panel">
          <p className="recipient-picker__panel-title">Add recipients</p>

          <div className="recipient-picker__field">
            <label className="form-label" htmlFor="recipient-picker-mode">
              To
            </label>
            <select
              id="recipient-picker-mode"
              className="form-select"
              name="recipientPickerMode"
              value={pickerMode}
              disabled={disabled}
              onChange={(e) => {
                setPickerMode(e.target.value);
                setContactName("");
                setGetContact([]);
              }}
            >
              <option value="">Select recipient type</option>
              <option value="associate">Associates</option>
              <option value="group">Group / Team</option>
              <option value="office">Office</option>
            </select>
          </div>

          {pickerMode === "associate" ? (
            <div className="recipient-picker__field">
              <label className="form-label" htmlFor="recipient-picker-search">
                Find an associate by name
              </label>
              <input
                id="recipient-picker-search"
                className="form-control"
                type="search"
                name="contactName"
                placeholder="Start typing a name…"
                disabled={disabled}
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                autoComplete="off"
              />
            </div>
          ) : null}

          <div className="recipient-picker__browse">
            {browsePlaceholder ? (
              <div className="recipient-picker__browse--empty">{browsePlaceholder}</div>
            ) : (
              <ul className="recipient-picker__list">
                {pickerMode === "associate" &&
                  getContact.map((contact, index) => (
                    <li key={contact._id || index}>
                      <button
                        type="button"
                        className="recipient-picker__item"
                        disabled={disabled}
                        onClick={() => handleContactAssociate(contact)}
                      >
                        <RecipientAvatar
                          src={contact.image}
                          alt={`${contact.firstName} ${contact.lastName}`}
                          fallback={(contact.firstName || "?")[0]}
                        />
                        <span className="recipient-picker__meta">
                          <span className="recipient-picker__name">
                            {contact.firstName} {contact.lastName}
                          </span>
                          {contact.active_office ? (
                            <span className="recipient-picker__sub">{contact.active_office}</span>
                          ) : null}
                        </span>
                      </button>
                    </li>
                  ))}

                {pickerMode === "group" &&
                  contactGroupMember.map((item, index) => (
                    <li key={item._id || index}>
                      <button
                        type="button"
                        className="recipient-picker__item"
                        disabled={disabled}
                        onClick={() => handleGroupData(item)}
                      >
                        <RecipientAvatar src={null} fallback="G" alt="" />
                        <span className="recipient-picker__meta">
                          <span className="recipient-picker__name">{groupLabel(item)}</span>
                          <span className="recipient-picker__sub">{groupOfficeLabel(item)}</span>
                        </span>
                      </button>
                    </li>
                  ))}

                {pickerMode === "office" &&
                  organizationGet.data?.map((item) => (
                    <li key={item._id}>
                      <button
                        type="button"
                        className="recipient-picker__item"
                        disabled={disabled}
                        onClick={() => handleOffice(item)}
                      >
                        <RecipientAvatar src={null} fallback="O" alt="" />
                        <span className="recipient-picker__meta">
                          <span className="recipient-picker__name">
                            {item.tag ? `${item.tag} ` : ""}
                            {item.name}
                          </span>
                          <span className="recipient-picker__sub">Office</span>
                        </span>
                      </button>
                    </li>
                  ))}
              </ul>
            )}
          </div>
        </div>

        <div className="recipient-picker__panel">
          <p className="recipient-picker__panel-title">
            Selected recipients
            {selectedCount > 0 ? (
              <span className="recipient-picker__count">({selectedCount})</span>
            ) : null}
          </p>

          <div className="recipient-picker__selected">
            {selectedCount === 0 ? (
              <div className="recipient-picker__selected--empty">
                <i className="fi-users" aria-hidden />
                <span>No recipients selected yet.</span>
                <span className="mt-1">Add people from the left, or leave empty to email everyone.</span>
              </div>
            ) : (
              <>
                {contactsAssociate.map((contact, index) => (
                  <SelectedChip
                    key={contact._id || `a-${index}`}
                    avatarSrc={contact.image}
                    avatarFallback={(contact.firstName || "?")[0]}
                    name={`${contact.firstName || ""} ${contact.lastName || ""}`.trim()}
                    sub={contact.active_office}
                    disabled={disabled}
                    onRemove={() =>
                      setContactsAssociate((prev) => prev.filter((_, i) => i !== index))
                    }
                  />
                ))}
                {contactsGroups.map((contact, index) => (
                  <SelectedChip
                    key={contact._id || `g-${index}`}
                    avatarSrc={null}
                    avatarFallback="G"
                    name={groupLabel(contact)}
                    sub={groupOfficeLabel(contact)}
                    disabled={disabled}
                    onRemove={() =>
                      setContactsGroups((prev) => prev.filter((_, i) => i !== index))
                    }
                  />
                ))}
                {officeGroups.map((office, index) => (
                  <SelectedChip
                    key={office._id || `o-${index}`}
                    avatarSrc={null}
                    avatarFallback="O"
                    name={`${office.tag || ""} ${office.name || ""}`.trim()}
                    sub="Office"
                    disabled={disabled}
                    onRemove={() =>
                      setOfficeGroups((prev) => prev.filter((_, i) => i !== index))
                    }
                  />
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
